import { zodResolver } from '@hookform/resolvers/zod';
import * as m from '@staiter/i18n';
import { AcceptInviteRequest, type AcceptInviteRequest as InviteReq } from '@staiter/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '~/components/primitives/Button';
import { Input } from '~/components/primitives/Input';
import { acceptInvite, getInvitePreview } from '~/lib/api/auth';
import { ApiRequestError } from '~/lib/api/client';
import { DEFAULT_LANDING_ROUTE } from '~/lib/rbac';
import { useAuthStore } from '~/store/auth';
import styles from './accept-invite.module.css';

const SearchSchema = z.object({
  token: z.string().min(1).optional(),
});

const ROLE_LABELS: Record<string, () => string> = {
  admin: () => m.role_admin(),
  manager: () => m.role_manager(),
  accountant: () => m.role_accountant(),
  reviewer: () => m.role_reviewer(),
  sme: () => m.role_sme(),
  warehouse_operator: () => m.role_warehouse_operator(),
  production_planner: () => m.role_production_planner(),
  viewer: () => m.role_viewer(),
};
function humanRole(role: string): string {
  const fn = ROLE_LABELS[role];
  return fn ? fn() : role;
}

export const Route = createFileRoute('/accept-invite')({
  validateSearch: SearchSchema,
  component: AcceptInvitePage,
});

type Preview = {
  email: string;
  role: string;
  invited_by_email: string | null;
  entity_name: string | null;
  /** invite-multi-entity — компании и роль в каждой (optional: старый BE). */
  memberships?: Array<{ entity_name: string | null; role: string }>;
  expires_at: string;
};

type FatalReason = 'no_token' | 'invalid_or_expired' | 'email_taken' | 'network';

function AcceptInvitePage(): JSX.Element {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const setFromLogin = useAuthStore((s) => s.setFromLogin);

  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(!!token);
  const [fatal, setFatal] = useState<FatalReason | null>(token ? null : 'no_token');
  const [rateLimitSeconds, setRateLimitSeconds] = useState<number | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<InviteReq>({
    resolver: zodResolver(AcceptInviteRequest),
    defaultValues: { token: token ?? '', new_password: '', confirm_password: '' },
  });

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await getInvitePreview(token);
        if (!cancelled) setPreview(p);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiRequestError) {
          setFatal('invalid_or_expired');
        } else {
          setFatal('network');
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (values: InviteReq): Promise<void> => {
    setServerError(null);
    try {
      const resp = await acceptInvite({ ...values, token: token ?? '' });
      setFromLogin(resp);
      await navigate({ to: DEFAULT_LANDING_ROUTE as '/' });
    } catch (err) {
      if (err instanceof ApiRequestError) {
        if (err.status === 429 && err.retry_after) {
          setRateLimitSeconds(err.retry_after);
          startCountdown(err.retry_after, setRateLimitSeconds);
          return;
        }
        if (err.status === 409) {
          setFatal('email_taken');
          return;
        }
        if (err.status === 400) {
          setFatal('invalid_or_expired');
          return;
        }
        setServerError(err.message || m.accept_invite_error_generic());
        return;
      }
      setServerError(m.login_network_error());
    }
  };

  if (fatal) {
    return (
      <main className={styles.page}>
        <section className={styles.card}>
          <div className={styles.brand} aria-hidden="true">
            <img src="/favicon.svg" alt="" width={24} height={24} className={styles.brandLogo} />
            STAITER
          </div>
          <div className={styles.fatal}>
            <h1 className={styles.fatalTitle}>
              {fatal === 'no_token' && m.accept_invite_no_token_title()}
              {fatal === 'invalid_or_expired' && m.accept_invite_expired_title()}
              {fatal === 'email_taken' && m.accept_invite_taken_title()}
              {fatal === 'network' && m.accept_invite_network_title()}
            </h1>
            <p className={styles.fatalBody}>
              {fatal === 'no_token' && m.accept_invite_no_token_body()}
              {fatal === 'invalid_or_expired' && m.accept_invite_expired_body()}
              {fatal === 'email_taken' && m.accept_invite_taken_body()}
              {fatal === 'network' && m.accept_invite_network_body()}
            </p>
            <a href="/login" className={styles.fatalAction}>
              {m.accept_invite_back_to_login()}
            </a>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      <section className={styles.card} aria-labelledby="accept-invite-title">
        <div className={styles.brand} aria-hidden="true">
          <img src="/favicon.svg" alt="" width={24} height={24} className={styles.brandLogo} />
          STAITER
        </div>
        <h1 id="accept-invite-title" className={styles.title}>
          {m.accept_invite_title()}
        </h1>
        <p className={styles.lede}>{m.accept_invite_lede()}</p>

        {previewLoading || !preview ? (
          <div className={styles.skeleton} aria-busy="true" aria-live="polite">
            <div className={styles.skeletonBar} />
            <div className={styles.skeletonBar} />
            <div className={styles.skeletonBar} />
          </div>
        ) : (
          <div className={styles.previewBlock}>
            <div className={styles.previewRow}>
              <span className={styles.previewLabel}>{m.accept_invite_label_email()}</span>
              <span className={styles.previewValue}>{preview.email}</span>
            </div>
            {preview.memberships && preview.memberships.length > 0 ? (
              // invite-multi-entity — приглашение в несколько компаний с ролью в каждой.
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>{m.accept_invite_label_companies()}</span>
                <span
                  className={styles.previewValue}
                  style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                  {preview.memberships.map((mb) => (
                    <span key={`${mb.entity_name ?? ''}:${mb.role}`}>
                      {mb.entity_name ? `${mb.entity_name} — ` : ''}
                      {humanRole(mb.role)}
                    </span>
                  ))}
                </span>
              </div>
            ) : (
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>{m.accept_invite_label_role()}</span>
                <span className={styles.previewValue}>{humanRole(preview.role)}</span>
              </div>
            )}
            {preview.invited_by_email && (
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>{m.accept_invite_label_invited_by()}</span>
                <span className={styles.previewValue}>{preview.invited_by_email}</span>
              </div>
            )}
          </div>
        )}

        <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
          <Input
            label={m.accept_invite_new_password()}
            type="password"
            autoComplete="new-password"
            autoFocus
            hint={m.accept_invite_password_hint()}
            error={errors.new_password?.message}
            {...register('new_password')}
          />
          <Input
            label={m.accept_invite_confirm_password()}
            type="password"
            autoComplete="new-password"
            error={errors.confirm_password?.message}
            {...register('confirm_password')}
          />

          {rateLimitSeconds !== null && (
            <p className={styles.rateLimit} role="status" aria-live="polite">
              {m.login_rate_limit({ seconds: rateLimitSeconds })}
            </p>
          )}
          {serverError && (
            <p className={styles.serverError} role="alert">
              {serverError}
            </p>
          )}

          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting || rateLimitSeconds !== null || !preview}
            loading={isSubmitting}
          >
            {m.accept_invite_submit()}
          </Button>
        </form>
      </section>
    </main>
  );
}

function startCountdown(initial: number, setter: (v: number | null) => void): void {
  let remaining = initial;
  const interval = setInterval(() => {
    remaining -= 1;
    if (remaining <= 0) {
      clearInterval(interval);
      setter(null);
    } else {
      setter(remaining);
    }
  }, 1000);
}
